import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Auto-refresh on 401  (bug fix 2026-02, production only symptom):
//
// The backend issues a 1-hour ``access_token`` cookie and a 7-day ``refresh_token``
// cookie. Before this hook, once the access_token expired the browser dropped it
// silently and any subsequent API call surfaced a raw "Not authenticated" toast
// (e.g. clicking the "邮件发送报价单" button after leaving the tab open past 1h).
//
// This interceptor catches any 401 and transparently calls ``POST /auth/refresh``
// (which reads the still-valid refresh cookie and stamps a fresh access cookie
// via ``Set-Cookie``). On success we replay the original request; on refresh
// failure we fall through to /login so the user gets a clear entry point rather
// than a mysterious button that "does nothing".
//
// The ``_isRetry`` flag prevents infinite loops; refresh endpoint itself is
// short-circuited so a failed refresh doesn't ricochet.
// ---------------------------------------------------------------------------
let refreshInFlight = null;
// Public routes that do NOT require authentication — the response interceptor
// must not redirect to /login when a probe API call (e.g. ``/auth/me``) returns
// 401 while the user is on one of these pages. 2026-07 · fixes the reset-
// password page bouncing to /login the instant it loads.
const PUBLIC_PATHS = ["/login", "/reset-password", "/scan/"];
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (!original) return Promise.reject(error);
    const url = String(original.url || "");
    const status = error.response?.status;
    const isRefreshCall = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/logout");
    // ``/auth/me`` is a discovery probe used by AuthContext to detect whether
    // the visitor is logged in — a 401 here is expected on public pages and
    // must NOT trigger the refresh + redirect flow. Just surface the error
    // so AuthContext can set ``user = false``.
    const isMeProbe = url.endsWith("/auth/me");
    if (status !== 401 || original._isRetry || isRefreshCall || isMeProbe) {
      return Promise.reject(error);
    }
    original._isRetry = true;
    try {
      if (!refreshInFlight) {
        refreshInFlight = api.post("/auth/refresh").finally(() => { refreshInFlight = null; });
      }
      await refreshInFlight;
    } catch (_e) {
      // Do not force-redirect visitors who are already on a public route (login,
      // password reset, QR scan) — bouncing would tear down whatever they're
      // doing right now.
      const pathname = typeof window !== "undefined" ? window.location.pathname : "";
      const onPublicRoute = PUBLIC_PATHS.some((p) => pathname.startsWith(p));
      if (typeof window !== "undefined" && !onPublicRoute) {
        const next = window.location.pathname + window.location.search;
        window.location.replace(`/login?next=${encodeURIComponent(next)}`);
      }
      return Promise.reject(error);
    }
    return api(original);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
